import { CommonModule, Location } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { forkJoin, from } from "rxjs";
import { LoaderService } from "../../../shared/components/loader/loader.service";
import { HeaderComponent } from "../../../shared/layout/app/header/header.component";
import { LeftSideNavComponent } from "../../../shared/layout/app/left-menu/left-side-nav.component";
import { AuthBllService } from "../../auth/auth-bll.service";
import { ProfileBllService } from "../profile-bll.service";
import { CategoryModel, UserEventStatusRespModel } from "../profile.model";
import { UserService } from "../../../core/services/user.service";
import { ActivatedRoute, Router } from "@angular/router";
import { HomeBllService } from "../../home/home-bll.service";
import { EventDetailsResponseModel } from "../../home/home.model";

@Component({
  selector: "app-area-of-interest",
  imports: [HeaderComponent, CommonModule, LeftSideNavComponent],
  templateUrl: "./area-of-interest.component.html",
  styleUrls: ["./area-of-interest.component.scss"],
})
export class AreaOfInterestComponent implements OnInit {
  /**
   * Stores a list of area of interest identifiers that are prefilled for the user.
   * This array is typically populated based on the user's existing profile data.
   */
  prefilledUserAreaOfInterest: string[] = [];

  /**
   * An array of category models representing the user's selected areas of interest.
   * Used to store and manage the categories relevant to the user's profile.
   */
  areaOfInterest: CategoryModel[] = [];

  /**
   * Stores the list of selected area of interest IDs.
   * Each ID represents a specific area of interest chosen by the user.
   */
  selectedAreaOfInterestIds: string[] = [];

  /**
   * Stores the list of area of interest IDs that are prefilled for the user.
   * These IDs are used to initialize the user's selected areas of interest in the profile.
   */
  prefilledUserAreaOfInterestIds: string[] = [];

  userProfile: UserEventStatusRespModel["user_profile"] | null = null;

  eventDetails: EventDetailsResponseModel | null = null;

  /**
   * Creates an instance of AreaOfInterestComponent.
   *
   * @param loaderService - Service to control the loading indicator.
   * @param profileBLLService - Business logic layer service for profile operations.
   * @param location - Angular service for interacting with browser location.
   * @param snackBar - Material service for displaying snack bar notifications.
   */
  constructor(
    private loaderService: LoaderService,
    private profileBLLService: ProfileBllService,
    private userService: UserService,
    private location: Location,
    private snackBar: MatSnackBar,
    private authService: AuthBllService,
    private route: ActivatedRoute,
    private router: Router,
    private bllService: HomeBllService
  ) {}

  /**
   * Angular lifecycle hook that is called after the component's view has been initialized.
   * Asynchronously fetches the area of interest data when the component is initialized.
   *
   * @returns {Promise<void>} A promise that resolves when the area of interest data has been loaded.
   */
  async ngOnInit(): Promise<void> {
    await this.getAreaOfInterestData();

    this.loadEventDetails();
  }

  /**
   * Fetches the user's event status and area of interest data concurrently.
   *
   * - Shows a loader while fetching data.
   * - Updates the user profile and area of interest information upon successful retrieval.
   * - Sets the user details in the user service, including name, profile image, mobile, state, and city.
   * - Populates the list of area of interest categories and their corresponding IDs if available.
   * - Handles errors by hiding the loader and logging the error to the console.
   *
   * @returns {Promise<void>} Resolves when the data fetching and processing is complete.
   */
  async getAreaOfInterestData() {
    this.loaderService.show();

    forkJoin({
      userEventStatus: from(this.profileBLLService.submitGetUserEventStatus()),
      areaOfInterest: from(this.profileBLLService.getAreaOfInterestData()),
    }).subscribe({
      next: (response) => {
        this.loaderService.hide();

        this.userProfile = response.userEventStatus.data.user_profile;
        if (this.userProfile) {
          const previewLogo =
            this.userProfile.image_url ??
            "/assets/images/Default-profile-photo.png";
          const userName = `${this.userProfile.first_name} ${this.userProfile.last_name}`;
          this.userService.setUser({
            name: userName,
            profileImg: previewLogo,
            mobile: this.userProfile.mobile,
            state: this.userProfile.state,
            city: this.userProfile.city,
            hasUnassignedTickets:
              response.userEventStatus.data.event_my_tickets
                .unassigned_tickets_bought_by_me > 0,
            hasAssignedTickets: response.userEventStatus.data.event_my_tickets.assigned_tickets_bought_by_me > 0 || response.userEventStatus.data.event_my_tickets.tickets_assigned_for_me > 0,
          });
        }

        this.areaOfInterest = response.areaOfInterest?.data?.list ?? [];

        if (response.userEventStatus.data.user_profile.category != null) {
          this.prefilledUserAreaOfInterest =
            response.userEventStatus.data.user_profile.category.split(",");

          this.prefilledUserAreaOfInterestIds = this.prefilledUserAreaOfInterest
            .map((catName) => {
              const category = this.areaOfInterest.find(
                (cat) => cat.name === catName
              );
              return category ? category.id.toString() : null;
            })
            .filter((id) => id !== null);
        }
      },
      error: (error) => {
        this.loaderService.hide();
        console.error("Error fetching Area of Interest data:", error);
      },
    });
  }

  /**
   * Checks if a category is already selected by user
   */
  isSelectedAreaOfInterest(id: any): boolean {
    return (
      this.prefilledUserAreaOfInterestIds &&
      this.prefilledUserAreaOfInterestIds.includes(id.toString())
    );
  }

  /**
   * Handles selection and deselection of pavilion categories via checkbox.
   * Enforces max selection limit and triggers error modal when exceeded.
   *
   * @param event - DOM event from checkbox input
   * @param categoryId - Numeric ID of the category being toggled (converted to string for internal use)
   */
  onAreaOfInterestCheckboxChange(event: Event, categoryId: number): void {
    const checkbox = event.target as HTMLInputElement;
    const id = categoryId.toString();

    if (checkbox.checked) {
      // Add the category if it was selected
      this.selectedAreaOfInterestIds.push(id);
    } else {
      // Remove the category if it was deselected
      this.selectedAreaOfInterestIds = this.selectedAreaOfInterestIds.filter(
        (pid) => pid !== id
      );

      this.prefilledUserAreaOfInterestIds =
        this.prefilledUserAreaOfInterestIds.filter((pid) => pid !== id);
    }

    this.selectedAreaOfInterestIds = Array.from(
      new Set([
        ...this.selectedAreaOfInterestIds,
        ...this.prefilledUserAreaOfInterestIds,
      ])
    );
  }

  /**
   * Handles the continue action for updating the user's area of interest.
   *
   * - Shows a loading indicator.
   * - Calls the profileBLLService to update the selected areas of interest.
   * - On success:
   *   - Logs a success message.
   *   - Saves a flag indicating the category has been filled in storage.
   *   - Hides the loader.
   *   - Navigates back to the previous location.
   * - On failure:
   *   - Hides the loader.
   *   - Logs an error message.
   *   - Displays a snackbar notification to the user.
   */
  onContinue() {
    this.loaderService.show();
    const activeTab = this.getActivetabFromRoute();
    if (this.selectedAreaOfInterestIds.join().length === 0) {
      this.loaderService.hide();
      if (activeTab === "areaofinterest") {
        this.router.navigate(["catalogue/pavilion-list"], {
          queryParams: { tab: "areaofinterest" },
        });
      } else {
        this.location.back();
      }
      return;
    }
    this.profileBLLService
      .updateAreaOfInterest(this.selectedAreaOfInterestIds.join())
      .then(() => {
        this.authService.saveStorageValue("hasCategoryFilled", "true");
        this.userService.setHasFilledUserCategory(true);

        if (activeTab === "areaofinterest") {
          setTimeout(() => {
            this.loaderService.hide();
            this.router.navigate(["catalogue/pavilion-list"], {
              queryParams: { tab: "areaofinterest" },
            });
          }, 1000);
        } else {
          setTimeout(() => {
            this.loaderService.hide();
            this.location.back();
          }, 1000);
        }
      })
      .catch(() => {
        this.loaderService.hide();
        console.error("Error updating Area of Interest");
        this.snackBar.open(
          "Failed to update Area of Interest. Please try again.",
          "Close",
          {
            duration: 4000,
          }
        );
      });
  }

  getActivetabFromRoute() {
    return this.route.snapshot.queryParamMap.get("tab");
  }

  loadEventDetails() {
    this.bllService.fetchEventDetails()
      .then((response) => {
        this.eventDetails = response.data;     
        this.userService.setLeftMenu({
          allow_to_register: this.eventDetails.allow_to_register,
          show_digital_catalog: this.eventDetails.show_digital_catalog,
          event_status: this.eventDetails.event_status
        });       
      })
      .catch((error) => {
        console.error('Error fetching event details:', error);
      });
  }
}
